import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SonaAnalytics } from '@sona/angular-util-analytics';
import { useLanguage } from '@sona/angular-util-common';
import { ENVIRONMENT } from '@sona/angular-util-environment';
import { isAuthApiError, VerifyTokenHashParams } from '@supabase/supabase-js';
import { toast } from 'ngx-sonner';
import { firstValueFrom } from 'rxjs';

import { SUPABASE } from '../../tokens/supabase.token';
import { AuthState } from '../auth.state';

/**
 * A facade for the general authentication
 */
@Injectable({
    providedIn: 'root'
})
export class SonaAuthFacade {

    /**
     * Whether the user is authenticated
     *
     * @param state
     * @param session
     */
    static readonly isAuthenticated = AuthState.isAuthenticated;

    /**
     * The authenticated user
     */
    static readonly user = AuthState.user;

    /**
     * The permissions of the authenticated user
     */
    static readonly features = AuthState.features;
    static readonly usageLimits = AuthState.usageLimits;
    static readonly usage = AuthState.usage;
    static readonly subscriptionPlan = AuthState.subscriptionPlan;
    static readonly subscriptionStatus = AuthState.subscriptionStatus;

    private readonly _environment = inject(ENVIRONMENT);
    private readonly _supabase = inject(SUPABASE);
    private readonly _router = inject(Router);
    private readonly _httpClient = inject(HttpClient);
    private readonly _analytics = inject(SonaAnalytics);

    readonly language = useLanguage();

    async signUp(email: string, password: string) {
        await this._analytics.track('sign-up', {
            provider: 'google'
        });

        const { data, error } = await this._supabase.auth.signUp({
            email,
            password,
            options: {
                data: { lang: this.language() }
            }
        });

        if (error) {
            this._handleError(error);
            throw error;
        }

        return data;
    }

    async signUpWithPassword(email: string, password: string) {
        await this._analytics.track('sign-up', {
            provider: 'password'
        });

        const { data, error } = await this._supabase.auth.signUp({
            email,
            password,
            options: {
                data: { lang: this.language() }
            }
        });

        if (error) {
            this._handleError(error);
            throw error;
        }

        return data;
    }

    async confirmSignIn(token: string, type: VerifyTokenHashParams['type']) {
        await this._analytics.track('confirm-sign-up', {
            provider: 'google'
        });

        const { data, error } = await this._supabase.auth.verifyOtp({
            token_hash: token,
            type,
            options: {
                redirectTo: location.origin
            }

        } as VerifyTokenHashParams);

        if (error) {
            this._handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Confirms the sign-up
     *
     * @param token - The token hash provided by Supabase
     * @param type - The type of the token
     * @param newsletterAccepted - Whether the user accepted the newsletter
     * @param termsAccepted - Whether the user accepted the terms
     */
    async confirmSignUp(token: string, type: 'signup' | 'invite', termsAccepted: boolean, newsletterAccepted: boolean) {
        await this._analytics.track('confirm-sign-up', {
            provider: 'google'
        });

        if (!([ 'signup', 'invite' ] as const).includes(type)) {
            throw new Error('Invalid type: only "signup" and "invite" are allowed. Received: ' + type);
        }

        if (!termsAccepted) {
            throw new Error('The terms must be accepted');
        }

        const { data, error } = await this._supabase.auth.verifyOtp({
            token_hash: token,
            type,
            options: {
                redirectTo: location.origin
            }
        } as VerifyTokenHashParams);

        if (error) {
            this._handleError(error);
            throw error;
        }

        await firstValueFrom(this._httpClient.post('/api/auth/accept-terms-and-newsletter', {
            newsletterAccepted,
            termsAcceptedVersion: this._environment.legal.terms.version
        }));

        return data;
    }

    /**
     * Triggers the login with Google flow
     */
    async signInWithGoogle() {
        void this._analytics.track('sign-in', {
            method: 'oauth',
            provider: 'google'
        });

        const { data, error } = await this._supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: location.origin
            }
        });

        if (error) {
            this._handleError(error);
            throw error;
        }

        return data;
    }

    /**
     * Triggers the One Time Password (OTP) login flow
     */
    async signInWithOtp(email: string) {
        void this._analytics.track('sign-in', {
            method: 'otp'
        });

        const { data, error } = await this._supabase.auth.signInWithOtp({
            email,
            options: {
                shouldCreateUser: true,
                data: { lang: this.language() }
            }
        });

        if (error) {
            this._handleError(error);
            throw error;
        }

        return data;
    }

    async signOut() {
        void this._analytics.track('sign-out');

        const { error } = await this._supabase.auth.signOut();

        if (error) {
            this._handleError(error);
            throw error;
        }

        void this._router.navigate([ '/auth/login' ]);
    }

    changeUserPassword(newPassword: string) {
        void this._analytics.track('change-password');

        try {
            return this._supabase.auth.updateUser({
                password: newPassword
            });
        } catch (error) {
            this._handleError(error);
            throw error;
        }
    }

    private _handleError(error: any) {
        if (isAuthApiError(error)) {
            const errorMessage = error.message || 'An unknown error occurred';
            toast.error(errorMessage);
            console.error('Auth error:', error);
        } else {
            toast.error('An unexpected error occurred');
            console.error('Unexpected error:', error);
        }
    }
}
